import { type MouseEvent, useState, useEffect } from 'react'
import { Box } from '@mui/material'
import { useFileUrlRefresh, type TRefreshFiles } from './hooks'
import {
  ImagePreview,
  PdfPreview,
  DocumentPreview,
  CsvPreview,
  ErrorState,
  FileModal,
  ChatFilePreview,
  CardFilePreview,
} from './components'
import { formatFileSize, getFileType } from './utils/utils'

export { formatFileSize }
export type { TRefreshFiles }

export type TFilePreviewProps = {
  file: {
    id: number
    name: string
    signedUrl: string
    size?: number | null
  }
  requestId: number
  variant?: 'card' | 'chat'
  onUrlRefresh?: (newUrl: string) => void
  refreshFiles?: TRefreshFiles
}

export const FilePreview = ({
  file,
  requestId,
  variant = 'card',
  onUrlRefresh,
  refreshFiles,
}: TFilePreviewProps) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const { currentUrl, hasError, isRefreshing, error, handleError, handleRetry, resetUrl } =
    useFileUrlRefresh({
      fileId: file.id,
      requestId,
      initialUrl: file.signedUrl,
      onUrlRefresh,
      refreshFiles,
    })

  useEffect(() => {
    resetUrl(file.signedUrl)
  }, [file.signedUrl, resetUrl])

  const fileType = getFileType(file.name)
  const isChat = variant === 'chat'

  const handleDownload = () => {
    window.open(currentUrl, '_blank')
  }

  const handleToggle = (e: MouseEvent) => {
    e.stopPropagation()
    if (fileType === 'pdf') {
      setModalOpen(true)
    } else {
      setExpanded(prev => !prev)
    }
  }

  const handleOpenInNew = (e: MouseEvent) => {
    e.stopPropagation()
    window.open(currentUrl, '_blank')
  }

  const renderPreview = () => {
    const commonProps = {
      url: currentUrl,
      variant,
      onError: handleError,
    }

    switch (fileType) {
      case 'image':
        return (
          <ImagePreview
            key={currentUrl}
            {...commonProps}
            name={file.name}
            isLoading={isRefreshing}
          />
        )
      case 'pdf':
        return <PdfPreview key={currentUrl} {...commonProps} name={file.name} />
      case 'csv':
        return <CsvPreview key={currentUrl} {...commonProps} />
      case 'audio':
        return (
          <Box key={currentUrl} component="audio" controls src={currentUrl} sx={{ width: '100%' }} />
        )
      case 'document':
      case 'other':
        return <DocumentPreview key={currentUrl} {...commonProps} />
      default:
        return null
    }
  }

  if (hasError) {
    return (
      <ErrorState
        onRetry={handleRetry}
        onDownload={handleDownload}
        isRefreshing={isRefreshing}
        error={error}
      />
    )
  }

  const preview = renderPreview()

  if (isChat) {
    return (
      <Box>
        <ChatFilePreview
          fileType={fileType}
          fileName={file.name}
          fileSize={file.size ? formatFileSize(file.size) : undefined}
          isRefreshing={isRefreshing}
          expanded={expanded}
          onToggle={handleToggle}
          onOpenInNew={handleOpenInNew}
          preview={preview}
        />
        {fileType === 'pdf' && (
          <FileModal open={modalOpen} onClose={() => setModalOpen(false)} fileName={file.name}>
            {preview}
          </FileModal>
        )}
      </Box>
    )
  }

  return (
    <>
      <CardFilePreview
        fileType={fileType}
        fileName={file.name}
        isRefreshing={isRefreshing}
        onClick={() => setModalOpen(true)}
      />
      <FileModal open={modalOpen} onClose={() => setModalOpen(false)} fileName={file.name}>
        {preview}
      </FileModal>
    </>
  )
}
